import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import { useUserData } from '../queries';
import { useMarketplaceConfig } from '../queries/marketplace';

const isProductFlagEnabled = (flag?: boolean) => flag !== false;

const useProductAvailability = () => {
  const { isAuthenticated } = useAuth();
  const {
    data: userData,
    isSuccess: isUserDataSuccess,
    isError: isUserDataError,
    isFetching: isUserDataFetching,
    refetch: refetchUserData,
  } = useUserData();
  const {
    data: marketplaceData,
    isError: isMarketplaceConfigError,
    isSuccess: isMarketplaceConfigSuccess,
    refetch: refetchMarketplaceConfig,
    isFetching: isMarketplaceConfigFetching,
  } = useMarketplaceConfig();

  const marketplaceProducts = marketplaceData?.marketplaceConfig.products;
  const userProducts = userData?.products;
  const hasUsableUserData = Boolean(isUserDataSuccess || userData);
  const isUserAvailabilityResolved = !isAuthenticated || hasUsableUserData;

  const isSportsbookEnabledByMarketplace = isProductFlagEnabled(marketplaceProducts?.sportsbook);
  const isCasinoEnabledByMarketplace = isProductFlagEnabled(marketplaceProducts?.casino);
  const isSportsbookEnabledByUser =
    !isAuthenticated || !hasUsableUserData || isProductFlagEnabled(userProducts?.sportsbook);
  const isCasinoEnabledByUser = !isAuthenticated || !hasUsableUserData || isProductFlagEnabled(userProducts?.casino);

  const hasSportsbookAccess = isSportsbookEnabledByMarketplace && isSportsbookEnabledByUser;
  const hasCasinoAccess = isCasinoEnabledByMarketplace && isCasinoEnabledByUser;
  const isMarketplaceAvailabilityResolved = Boolean(isMarketplaceConfigSuccess || isMarketplaceConfigError);
  const isProductAvailabilityResolved = isMarketplaceAvailabilityResolved && isUserAvailabilityResolved;

  const hasMarketplaceAccessError = !marketplaceData && isMarketplaceConfigError;
  const hasProfileAccessError = isAuthenticated && !hasUsableUserData && isUserDataError;
  const hasCasinoAccessError = Boolean(hasMarketplaceAccessError || hasProfileAccessError);
  const isCasinoAccessLoading = !hasCasinoAccessError && (!marketplaceData || !isProductAvailabilityResolved);
  const requiresCasinoSignIn = Boolean(
    hasCasinoAccess && marketplaceData?.casinoConfig?.requiresAuthentication === true && !isAuthenticated
  );
  const canViewCasinoContent = Boolean(
    marketplaceData && isProductAvailabilityResolved && hasCasinoAccess && !requiresCasinoSignIn
  );
  const isRetryingCasinoAccess = Boolean(
    (hasMarketplaceAccessError && isMarketplaceConfigFetching) || (hasProfileAccessError && isUserDataFetching)
  );

  const retryCasinoAccess = () =>
    Promise.all([
      ...(hasMarketplaceAccessError ? [refetchMarketplaceConfig()] : []),
      ...(hasProfileAccessError ? [refetchUserData()] : []),
    ]);

  return {
    isCasinoOnlyProduct: hasCasinoAccess && !hasSportsbookAccess,
    isSportsbookOnlyProduct: hasSportsbookAccess && !hasCasinoAccess,
    hasSportsbookAccess,
    hasCasinoAccess,
    canViewCasinoContent,
    requiresCasinoSignIn,
    hasCasinoAccessError,
    isCasinoAccessLoading,
    retryCasinoAccess,
    isRetryingCasinoAccess,
    isAuthenticated,
    isProductAvailabilityResolved,
  };
};

export default useProductAvailability;
